import { useState } from 'react'
import { useEffect } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Label,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'

import Toggle from '../../../Toggle/index'
import ChartBox from '../ChartBox/index'
import { chartColors, chartMainColor } from '../chartHelpers'
import InsightsTooltip from '../InsightsTooltip/index'
import styles from './MultiBar.module.scss'

const LegendItem = ({ name, color, toggleValue, setToggleValue, isToggleDisabled }) => (
  <label className={styles.legend}>
    <svg width="10" height="10">
      <rect x="0" y="0" width="10" height="10" fill={color} />
    </svg>
    {name}
    {toggleValue !== null && (
      <Toggle
        className={styles.legendToggle}
        checked={toggleValue}
        handleChange={() => setToggleValue(!toggleValue)}
        disabled={isToggleDisabled}
      />
    )}
  </label>
)

const MultiBarCard = ({
  barSize,
  title,
  subtitle,
  data,
  xLabel,
  yLabel,
  height = 300,
  hasLegends,
  hasToggles,
  noWrapper,
  customYLabel,
  unit,
  handleChangeFilteredProperties
}) => {
  const longestProperties = Object.values(data).reduce(
    (prev, curr) => (Object.keys(curr).length > Object.keys(prev).length ? curr : prev),
    {}
  )

  const graphProperties = Object.keys(longestProperties).filter(k => k !== 'name')

  const [filteredProperties, setFilteredProperties] = useState(graphProperties)

  useEffect(() => {
    if (handleChangeFilteredProperties) {
      handleChangeFilteredProperties(filteredProperties)
    }
  }, [filteredProperties, handleChangeFilteredProperties])

  const colors = graphProperties.reduce((prev, curr, i) => {
    return { ...prev, [curr]: chartColors[i] }
  }, {})

  return (
    <ChartBox noWrapper={noWrapper}>
      <ChartBox.Header title={title} subtitle={subtitle}>
        {(hasLegends || hasToggles) && (
          <div className={styles.legends}>
            {graphProperties.map((k, i, arr) => (
              <LegendItem
                key={k}
                name={k}
                color={arr.length > 1 ? chartColors[i] : chartMainColor}
                toggleValue={hasToggles ? filteredProperties.includes(k) : null}
                isToggleDisabled={
                  filteredProperties.length === 1 && filteredProperties.includes(k)
                }
                setToggleValue={val =>
                  val
                    ? setFilteredProperties([...filteredProperties, k])
                    : setFilteredProperties(filteredProperties.filter(v => v !== k))
                }
              />
            ))}
          </div>
        )}
      </ChartBox.Header>
      <ChartBox.Content>
        {customYLabel && <div className={styles.yLabel}>{yLabel}</div>}
        <ResponsiveContainer width="100%" height={height}>
          <BarChart
            data={data}
            barGap={2}
            margin={{
              top: 30
            }}
          >
            <XAxis
              dataKey="name"
              axisLine={{ stroke: '#F2F2F2', strokeWidth: 1 }}
              tick={{
                fill: '#7D7D7D',
                fontSize: 12,
                transform: 'translate(0, 14)'
              }}
              tickSize={0}
              padding={{ top: 30, bottom: 20, right: xLabel ? 40 : 0 }}
            >
              {xLabel && (
                <Label
                  value={xLabel}
                  position="insideBottomRight"
                  fontSize="12"
                  fontWeight={500}
                  fill="#7D7D7D"
                  offset={6}
                />
              )}
            </XAxis>
            <YAxis
              tickSize={0}
              axisLine={false}
              tick={{
                fill: '#7D7D7D',
                fontSize: 12,
                transform: 'translate(-10, 0)'
              }}
              padding={{ top: yLabel ? 18 : 0, bottom: 0 }}
            >
              {yLabel && !customYLabel && (
                <Label
                  value={yLabel}
                  position="insideTop"
                  fontSize="12"
                  fontWeight={500}
                  offset={-20}
                  fill="#7D7D7D"
                />
              )}
            </YAxis>
            <CartesianGrid stroke="#F2F2F2" vertical={false} />
            <Tooltip
              cursor={{
                stroke: 'red',
                strokeWidth: 0,
                fill: '#dedede',
                opacity: 0.22
              }}
              labelFormatter={value => `${value} ${xLabel ? xLabel : ''}`}
              labelStyle={{ marginBottom: 10, fontWeight: 500 }}
              itemStyle={{ fontSize: 13 }}
              animationEasing="ease"
              animationDuration={500}
              content={<InsightsTooltip />}
            />
            {graphProperties
              .filter(p => filteredProperties.includes(p))
              .map(key => (
                <Bar
                  unit={unit}
                  dataKey={key}
                  barSize={barSize}
                  fill={graphProperties.length > 1 ? colors[key] : chartColors[1]}
                  key={`bar-${key}`}
                />
              ))}
          </BarChart>
        </ResponsiveContainer>
      </ChartBox.Content>
    </ChartBox>
  )
}

export default MultiBarCard
